// The Summary row's figure rules -- the Time-Weighted Return, the value
// tiles' date notes, and the Long Value scope's six cards. Pure, so the rules
// are testable without the report; useSummaryCards holds the state they read.
import { BucketSingleInfoCards, BucketTotals } from "helpers/types";
import { BucketSummaryEnum, BucketTwrFormula } from "constants/index";
import { formatUsDate } from "helpers";

/**
 * A run's portfolio aggregate return as the Long Value scope reads it: the
 * figure, or the sentence saying why there is none.
 */
export interface AggregateReturn {
  value: number | null;
  unavailable?: string;
}

/**
 * What the Time-Weighted Return card shows for one scope, from that scope's
 * cards payload.
 *
 * `card_4` is null EXACTLY when `card_4_unavailable` carries a reason
 * (types.ts), and this is the one place that reading is turned into the two
 * props the card takes. A null becomes the EMPTY SENTINEL rather than being
 * passed through: the card reads N/A from the reason either way, but the
 * value is also what gets STORED, what the export prints and what survives
 * the report's remount -- so leaving the last run's number behind would
 * credit this scope with a return it never reported, on all three.
 *
 * A scope that produced a figure carries no reason, which is what keeps the
 * fix confined to the scopes that had none: a measured 0.00% is a result and
 * still reads as one.
 *
 * `card_4_unavailable` is absent on a job that COMPLETED before the field
 * shipped and is served from cache; such a payload carries `card_4: 0` and
 * reads exactly as it did before, which is the graceful half of typing it
 * optional rather than required.
 *
 * `formula` is the gear's pick, and the figure it names is read off
 * `card_4_by_formula` (operator, task 3764) -- which is what lets the pick
 * change the card with NO re-run, because every formula's answer arrived
 * with the run. Falling back to `card_4` covers the two payloads that carry
 * no such map: a job cached before it shipped, and any caller that does not
 * care which formula it is looking at. Both then read exactly as they did
 * before, showing the RUN's figure whichever way the gear is set.
 *
 * The fallback is on the ENTRY, not on the map: a scope present in the map
 * still refuses per formula, and `{value: null, unavailable: "..."}` is a
 * refusal to render as N/A rather than a miss to paper over with `card_4`.
 */
export const timeWeightedReturnFigure = (
  cards: Pick<
    BucketSingleInfoCards,
    "card_4" | "card_4_unavailable" | "card_4_by_formula"
  >,
  formula?: BucketTwrFormula,
  // NOT Pick<ReportValueCardProps, ...>: that type's `value` admits null so a
  // card can render a null arriving from a cached payload, and borrowing it
  // here published a nullable `value` this function cannot actually return --
  // the `?? ""` below has already turned the payload's null into the empty
  // sentinel. The stored figure it feeds is `number | string`, so the wider
  // borrowed type failed to compile against its own setter. Stating the
  // narrower truth keeps it assignable to the card props either way.
): { value: number | string; unavailableReason?: string } => {
  const picked =
    formula === undefined ? undefined : cards.card_4_by_formula?.[formula];
  if (picked) {
    return {
      value: picked.value ?? "",
      unavailableReason: picked.unavailable ?? undefined,
    };
  }
  return {
    value: cards.card_4 ?? "",
    unavailableReason: cards.card_4_unavailable ?? undefined,
  };
};

/**
 * What the Starting and Ending Value tiles say about WHERE their figure came
 * from, when the backend says the bucket did not cover the whole period
 * (operator, task 3730).
 *
 * A note appears only where `card_1_date`/`card_2_date` arrived, and types.ts
 * states when the backend sends them -- the reader's question is "why is this
 * figure not my period's", so a date repeated on every run that priced a
 * Monday open would be noise on top of the answer.
 *
 * Both dates absent is the ordinary case AND the shape a job cached before
 * they shipped carries, and the two want the same thing: no icon, no
 * tooltip, the figure alone. So `undefined` is returned per tile rather than
 * a sentence saying the date matches, and the caller sets BOTH tiles on
 * every scope change, which is what clears a previous scope's note.
 */
export const summaryValueDateNotes = (
  cards: Pick<BucketSingleInfoCards, "card_1_date" | "card_2_date"> | undefined,
): { startingValue?: string; endingValue?: string } => ({
  startingValue: cards?.card_1_date
    ? `As of ${formatUsDate(cards.card_1_date)}`
    : undefined,
  endingValue: cards?.card_2_date
    ? `As of ${formatUsDate(cards.card_2_date)}`
    : undefined,
});

/**
 * What the Long Value scope's value, income and margin cards say when the
 * payload carried no `long_value` entry to read them off (operator, tasks
 * 3636 and 4700).
 *
 * The shape that reaches: a job CACHED before the entry shipped, served to
 * this frontend for the rest of its TTL because the cache key is versioned
 * on the data and never on the code. The operator chose N/A over the old
 * sum-of-the-bucket-cards fallback, and the reason is the point of the task:
 * that sum is the wrong figure this change exists to stop showing, so
 * falling back to it would keep serving it to every cached job rather than
 * saying plainly that the report predates the answer.
 *
 * User-facing prose, shown on the card's info icon, so it names no function
 * and no module -- and says what to DO about it, since re-running the report
 * is what actually produces the figure.
 */
export const LONG_VALUE_VALUES_UNAVAILABLE =
  "This report was calculated before its Long Value figures could be " +
  "measured across all three buckets. Re-run the report to see them.";

/**
 * The Long Value scope's six figures as this page builds them.
 *
 * `BucketSingleInfoCards` with the value, income and margin cards widened to
 * carry the EMPTY SENTINEL, for the same reason timeWeightedReturnFigure narrows its
 * own return rather than borrowing the card's props: the figure here is what
 * gets stored, exported and survives a remount, and `""` is how this page
 * has always said "no figure" to them. `values_unavailable` is set EXACTLY
 * when those five are the sentinel -- the same invariant card_4 and
 * card_4_unavailable hold, and the caller relies on it to decide whether to
 * put a reason on the cards at all.
 */
export interface LongValueSummaryCards
  extends Omit<
    BucketSingleInfoCards,
    "card_1" | "card_2" | "card_3" | "card_5" | "card_6"
  > {
  card_1: number | string;
  card_2: number | string;
  card_3: number | string;
  card_5: number | string;
  card_6: number | string;
  values_unavailable?: string;
}

/** The three numbered scopes the drop-down offers, in its own order. */
const NUMBERED_BUCKET_SUMMARIES = [
  BucketSummaryEnum.BUCKET_1,
  BucketSummaryEnum.BUCKET_2,
  BucketSummaryEnum.BUCKET_3,
] as const;

/**
 * The Long Value scope's six summary cards: five read off the backend's
 * `long_value` entry, and the return filled in from the portfolio aggregate.
 *
 *   - **The three value cards are the BACKEND entry's** (operator, task
 *     3636). Summing the buckets' own cards could not make the screen add
 *     up: each bucket's card_1 is ITS OWN first priced day, so the total
 *     described a portfolio as of three different dates. The entry sources
 *     all three figures as of ONE date -- the period's requested start --
 *     counting a bucket that did not exist yet as the 0 it was worth, so the
 *     card agrees with the first point of the Long Value chart line.
 *   - **Income and margin are the BACKEND entry's too, and only the entry's**
 *     (operator, task 4700). Both are resolved where their rules live:
 *     income in `combined_scope_income` (every bucket's dividends plus the
 *     option cashflow of Bucket 2 alone, task 3574), margin as the report
 *     account's charges plus Bucket 3's -- which is skipped when Bucket 3
 *     names the report account, since margin is charged to an ACCOUNT and
 *     that charge is already pooled. This page used to rebuild margin from
 *     the bucket cards and fall back to a sum of the bucket incomes; it no
 *     longer derives either, so the card cannot disagree with the backend.
 *   - **card_4 is the portfolio's aggregate return** -- each bucket's return
 *     weighted by its share of the opening value -- filled in from the
 *     Diversify payload's `portfolio_aggregate_return` (task 3542).
 *
 * With no entry -- a job cached before it shipped, or one whose entry cards
 * the backend refused -- all five entry figures are the EMPTY SENTINEL and
 * `values_unavailable` says why (operator, tasks 3636 and 4700): N/A with a
 * reason, never a figure re-derived from the bucket cards.
 *
 * Returns null when the run produced no numbered bucket with cards, which the
 * caller reads as "nothing to apply" -- the same guard an absent scope gets,
 * so the cards keep whatever they held rather than dropping to zeroes no
 * calculation produced.
 */
export const longValueCards = (
  totals: BucketTotals,
  aggregateReturn: AggregateReturn,
): LongValueSummaryCards | null => {
  // `!= null`, not `!== undefined`: a bucket whose cards the backend refused
  // arrives as `null`, and is as absent as one that never ran (task 4417).
  const ran = NUMBERED_BUCKET_SUMMARIES.some(
    (summary) => totals[summary]?.single_info_cards != null,
  );
  if (!ran) return null;

  // CHAINED PAST `single_info_cards`, which the type declares nullable: the
  // backend builds it from a series it can refuse, and a TypeError here
  // blanks the whole report page.
  const entry = totals[BucketSummaryEnum.LONG_VALUE]?.single_info_cards;

  // card_4 is null EXACTLY when card_4_unavailable carries a reason -- the
  // invariant build_info_cards RAISES on backend-side. Here the refusal
  // simply wins: throwing over a malformed pair would blank the page instead
  // of one card.
  const refusal = aggregateReturn.unavailable;
  return {
    // The EMPTY SENTINEL rather than a stale number when the payload carried
    // no entry: the value is what gets stored, exported and survives a
    // remount, so leaving the previous run's figure behind would credit this
    // scope with a figure it never reported. `??` rather than `||` so a
    // genuine 0 (no income, no margin accrued) is reported as the 0 it is.
    card_1: entry?.card_1 ?? "",
    card_2: entry?.card_2 ?? "",
    card_3: entry?.card_3 ?? "",
    values_unavailable: entry ? undefined : LONG_VALUE_VALUES_UNAVAILABLE,
    card_4: refusal ? null : aggregateReturn.value,
    card_4_unavailable: refusal ?? null,
    card_5: entry?.card_5 ?? "",
    card_6: entry?.card_6 ?? "",
  };
};

// The two time-weighted return formulas the Summary gear offers. Neither
// names a date since task 3603 retired the one that did, so the label is a
// straight lookup; `startDate` stays in the signature because the caller has
// it and a date-bearing formula is the kind of thing that comes back.
const TWR_FORMULA_LABELS: Record<BucketTwrFormula, string> = {
  [BucketTwrFormula.DAILY_WEIGHT_ADJUSTMENT]: "Daily Weight Adjustment",
  [BucketTwrFormula.BUCKET_MARKET_VALUE]: "Bucket Market Value",
};

export const twrFormulaLabel = (
  formula: BucketTwrFormula,
  _startDate: string | undefined,
): string => TWR_FORMULA_LABELS[formula];
